/**
 * Services — détail des 4 services BCL Telecom + processus en 5 étapes.
 */
import {
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Cpu,
  FileCheck,
  Headphones,
  PhoneCall,
  Sparkles,
  Users,
  Wrench,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MarketingLayout } from '@/components/site/marketing-layout';

const services = [
  {
    title: 'Conseil technique',
    intro:
      'Étude de faisabilité et préconisations pour optimiser votre infrastructure télécom dès la phase projet.',
    icon: Cpu,
    bullets: [
      'Analyse du site et des contraintes (génie civil, copropriété, voirie)',
      'Étude de la couverture fibre existante et des opérateurs présents',
      'Préconisations matériels (PAR, PBO, fourreaux) et schémas d’implantation',
      'Estimation budgétaire détaillée par poste',
    ],
  },
  {
    title: 'Interlocuteur unique',
    intro:
      'Un chef de projet dédié pilote votre dossier de la qualification au PV de réception.',
    icon: PhoneCall,
    bullets: [
      'Un seul contact pour toutes vos questions techniques et administratives',
      'Reporting régulier sur l’avancement (par e-mail + espace client)',
      'Coordination des intervenants (architecte, opérateur, copropriété)',
      'Disponibilité 7j/7 pendant les phases chantier',
    ],
  },
  {
    title: 'Gestion administrative',
    intro:
      'Nous prenons en charge l’intégralité des démarches auprès des opérateurs et des autorités.',
    icon: FileCheck,
    bullets: [
      'Dépôt et suivi des dossiers de raccordement chez Orange / SFR / Free',
      'Demandes d’autorisation de voirie auprès des collectivités',
      'Conventions de servitude et accords de copropriété',
      'Constitution du dossier ARCEP si nécessaire',
    ],
  },
  {
    title: 'Suivi de projet',
    intro:
      'Supervision complète du cycle de vie de votre infrastructure jusqu’à la mise en service.',
    icon: Users,
    bullets: [
      'Planification et coordination des techniciens',
      'Comptes rendus de visite et rapports d’avancement',
      'Validation des étapes critiques (pré-visite, génie civil, raccordement)',
      'PV de réception et garantie de bonne fin',
    ],
  },
];

const process = [
  {
    step: '01',
    title: 'Qualification',
    description:
      'Créez votre compte et décrivez votre projet en quelques étapes. Nous vous proposons un pack adapté.',
    icon: ClipboardList,
  },
  {
    step: '02',
    title: 'Étude technique',
    description:
      'Notre bureau d’études analyse votre site et constitue le dossier technique complet.',
    icon: Cpu,
  },
  {
    step: '03',
    title: 'Validation client',
    description:
      'Vous validez le devis, déposez les pièces justificatives et signez la convention dans votre espace client.',
    icon: CheckCircle2,
  },
  {
    step: '04',
    title: 'Travaux & raccordement',
    description:
      'Coordination des techniciens et des opérateurs jusqu’au branchement final.',
    icon: Wrench,
  },
  {
    step: '05',
    title: 'PV de réception',
    description:
      'Procès-verbal signé, garantie de bonne fin, mise à disposition du compte rendu dans votre espace client.',
    icon: Sparkles,
  },
];

export function ServicesPage() {
  return (
    <MarketingLayout>
      <section className="bg-gradient-to-b from-white to-bcl-grey/40 py-20">
        <div className="container px-4 mx-auto">
          <div className="mx-auto max-w-3xl text-center">
            <div className="inline-block rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-bcl-blue mb-4">
              Nos services
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-balance">
              Tout le raccordement fibre, sous un seul toit
            </h1>
            <p className="text-lg text-gray-600">
              Du conseil technique au PV de réception, BCL Telecom couvre
              l&apos;intégralité de la chaîne d&apos;adduction télécoms pour vos
              projets immobiliers.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/signup">
                <Button size="lg">
                  Démarrer mon projet
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/offres">
                <Button size="lg" variant="outline">Voir nos packs</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 4 SERVICES DETAILLES */}
      <section className="py-20 bg-white">
        <div className="container px-4 mx-auto">
          <div className="grid gap-6 md:grid-cols-2">
            {services.map((s) => (
              <Card key={s.title} className="border-gray-100 hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="grid h-12 w-12 place-items-center rounded-md bg-bcl-blue/10 text-bcl-blue mb-2">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <CardTitle className="text-xl">{s.title}</CardTitle>
                  <CardDescription>{s.intro}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-bcl-teal" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESSUS */}
      <section className="py-20 bg-bcl-grey/30">
        <div className="container px-4 mx-auto">
          <div className="mx-auto max-w-2xl text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Notre processus</h2>
            <p className="text-lg text-gray-600">
              5 étapes claires de la création de compte au PV de réception.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            {process.map((p) => (
              <div key={p.step} className="relative rounded-xl border border-gray-100 bg-white p-5">
                <p className="text-3xl font-bold text-bcl-blue/20 mb-2">{p.step}</p>
                <div className="grid h-10 w-10 place-items-center rounded-md bg-bcl-blue/10 text-bcl-blue mb-3">
                  <p.icon className="h-5 w-5" />
                </div>
                <h3 className="font-semibold mb-1">{p.title}</h3>
                <p className="text-xs text-gray-600">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="container px-4 mx-auto max-w-4xl text-center">
          <div className="grid h-12 w-12 mx-auto place-items-center rounded-full bg-bcl-blue/10 text-bcl-blue mb-4">
            <Headphones className="h-6 w-6" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">
            Vous avez une question technique ?
          </h2>
          <p className="text-gray-600 mb-6">
            Notre équipe vous répond sous 24 h ouvrées. Joignez-nous par
            formulaire ou téléphone.
          </p>
          <Link to="/contact">
            <Button size="lg">Contacter un conseiller</Button>
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
